import {
  normalizeDualCtaLang,
  type DualCtaLang,
} from "../../shared/dualCtaLang";
import {
  MEMBERSHIP_FREE_CTA,
  MEMBERSHIP_VIP_CTA,
} from "@/lib/membershipCtaCopy";

type LandingStringKey =
  | "vipLabel"
  | "freeLabel"
  | "processing"
  | "loading"
  | "unavailable"
  | "notAvailable"
  | "vipSubtitle"
  | "freeSubtitle"
  | "securePaymentWith"
  | "engagedNudge"
  | "tapToPlay"
  | "videoNetworkHint"
  | "funnelHeadline"
  | "funnelSubheadline"
  | "benefitExclusive"
  | "benefitInstant"
  | "benefitFlexible"
  | "trustChipExclusive"
  | "trustChipInstant"
  | "trustChipSecure"
  | "trustLine"
  | "pricePerMonth";

const STRINGS: Record<LandingStringKey, Record<DualCtaLang, string>> = {
  vipLabel: {
    es: MEMBERSHIP_VIP_CTA.es,
    en: MEMBERSHIP_VIP_CTA.en,
    pt: MEMBERSHIP_VIP_CTA.pt,
    fr: MEMBERSHIP_VIP_CTA.fr,
    de: MEMBERSHIP_VIP_CTA.de,
    it: MEMBERSHIP_VIP_CTA.it,
  },
  freeLabel: {
    es: MEMBERSHIP_FREE_CTA.es,
    en: MEMBERSHIP_FREE_CTA.en,
    pt: MEMBERSHIP_FREE_CTA.pt,
    fr: MEMBERSHIP_FREE_CTA.fr,
    de: MEMBERSHIP_FREE_CTA.de,
    it: MEMBERSHIP_FREE_CTA.it,
  },
  processing: {
    es: "Procesando…",
    en: "Processing…",
    pt: "Processando…",
    fr: "Traitement…",
    de: "Verarbeitung…",
    it: "Elaborazione…",
  },
  loading: {
    es: "Cargando…",
    en: "Loading…",
    pt: "Carregando…",
    fr: "Chargement…",
    de: "Laden…",
    it: "Caricamento…",
  },
  unavailable: {
    es: "No disponible",
    en: "Not available",
    pt: "Indisponível",
    fr: "Indisponible",
    de: "Nicht verfügbar",
    it: "Non disponibile",
  },
  notAvailable: {
    es: "No disponible",
    en: "Not available",
    pt: "Indisponível",
    fr: "Indisponible",
    de: "Nicht verfügbar",
    it: "Non disponibile",
  },
  vipSubtitle: {
    es: "contenido exclusivo · acceso VIP",
    en: "exclusive content · VIP access",
    pt: "conteúdo exclusivo · acesso VIP",
    fr: "contenu exclusif · accès VIP",
    de: "exklusive Inhalte · VIP-Zugang",
    it: "contenuto esclusivo · accesso VIP",
  },
  freeSubtitle: {
    es: "Sin cargo",
    en: "No charge",
    pt: "Sem custo",
    fr: "Sans frais",
    de: "Kostenlos",
    it: "Senza costi",
  },
  securePaymentWith: {
    es: "Pago seguro con",
    en: "Secure payment with",
    pt: "Pagamento seguro com",
    fr: "Paiement sécurisé avec",
    de: "Sichere Zahlung mit",
    it: "Pagamento sicuro con",
  },
  engagedNudge: {
    es: "¿Listo? Un toque y sos miembro",
    en: "Ready? One tap to join",
    pt: "Pronto? Um toque e você é membro",
    fr: "Prêt ? Un tap pour rejoindre",
    de: "Bereit? Ein Tipp und du bist dabei",
    it: "Pronto? Un tocco e sei membro",
  },
  tapToPlay: {
    es: "Tocá el video para reproducir",
    en: "Tap the video to play",
    pt: "Toque no vídeo para reproduzir",
    fr: "Touchez la vidéo pour lire",
    de: "Tippe auf das Video zum Abspielen",
    it: "Tocca il video per riprodurre",
  },
  videoNetworkHint: {
    es: "Si no carga, probá sin VPN o desactivá el bloqueador de anuncios",
    en: "If it won't load, try without VPN or disable your ad blocker",
    pt: "Se não carregar, tente sem VPN ou desative o bloqueador de anúncios",
    fr: "Si ça ne charge pas, essayez sans VPN ou désactivez votre bloqueur de pub",
    de: "Wenn es nicht lädt, ohne VPN versuchen oder Werbeblocker deaktivieren",
    it: "Se non carica, prova senza VPN o disattiva il blocco annunci",
  },
  funnelHeadline: {
    es: "Accedé al contenido exclusivo",
    en: "Get exclusive access",
    pt: "Acesse o conteúdo exclusivo",
    fr: "Accédez au contenu exclusif",
    de: "Exklusiven Zugang erhalten",
    it: "Accedi ai contenuti esclusivi",
  },
  funnelSubheadline: {
    es: "Mirá el video y unite en un toque — acceso VIP al instante.",
    en: "Watch the preview and join in one tap — instant VIP access.",
    pt: "Assista ao vídeo e entre com um toque — acesso VIP na hora.",
    fr: "Regardez la vidéo et rejoignez en un tap — accès VIP instantané.",
    de: "Schau das Video und tritt mit einem Tipp bei — sofortiger VIP-Zugang.",
    it: "Guarda il video e unisciti con un tap — accesso VIP immediato.",
  },
  benefitExclusive: {
    es: "Contenido VIP que no está en redes",
    en: "VIP content you won't find on social",
    pt: "Conteúdo VIP que não está nas redes",
    fr: "Contenu VIP introuvable sur les réseaux",
    de: "VIP-Inhalte, die es in Social Media nicht gibt",
    it: "Contenuti VIP che non trovi sui social",
  },
  benefitInstant: {
    es: "Acceso inmediato después del pago",
    en: "Instant access right after checkout",
    pt: "Acesso imediato após o pagamento",
    fr: "Accès immédiat après le paiement",
    de: "Sofortiger Zugang nach der Zahlung",
    it: "Accesso immediato dopo il pagamento",
  },
  benefitFlexible: {
    es: "Cancelás cuando quieras, sin compromiso",
    en: "Cancel anytime — no long-term lock-in",
    pt: "Cancele quando quiser, sem compromisso",
    fr: "Annulez quand vous voulez, sans engagement",
    de: "Jederzeit kündbar, ohne Bindung",
    it: "Annulla quando vuoi, senza vincoli",
  },
  trustChipExclusive: {
    es: "Exclusivo",
    en: "Exclusive",
    pt: "Exclusivo",
    fr: "Exclusif",
    de: "Exklusiv",
    it: "Esclusivo",
  },
  trustChipInstant: {
    es: "Al instante",
    en: "Instant",
    pt: "Na hora",
    fr: "Instantané",
    de: "Sofort",
    it: "Subito",
  },
  trustChipSecure: {
    es: "Pago seguro",
    en: "Secure pay",
    pt: "Pagamento seguro",
    fr: "Paiement sécurisé",
    de: "Sichere Zahlung",
    it: "Pagamento sicuro",
  },
  trustLine: {
    es: "Pago seguro · Acceso al instante",
    en: "Secure checkout · Instant access",
    pt: "Pagamento seguro · Acesso imediato",
    fr: "Paiement sécurisé · Accès immédiat",
    de: "Sichere Zahlung · Sofortiger Zugang",
    it: "Pagamento sicuro · Accesso immediato",
  },
  pricePerMonth: {
    es: "/ mes",
    en: "/ month",
    pt: "/ mês",
    fr: "/ mois",
    de: "/ Monat",
    it: "/ mese",
  },
};

/** Texto default del fullscreen Dual CTA cuando no hay override admin/variante. */
export function dualCtaLandingText(
  key: LandingStringKey,
  language: string | undefined | null,
): string {
  const lang = normalizeDualCtaLang(language);
  return STRINGS[key][lang] ?? STRINGS[key].es;
}